import { getMovementTypeLabel } from '../../../../../../constants/inventoryLabels'
import { formatQuantity } from './inventoryFormatters'
import {
  resolveMovementQuantity,
  resolveMovementReference,
  resolveMovementType,
  resolveWarehouseLabel
} from './inventoryResolvers'

const INITIAL_LOAD_GROUP_WINDOW_MS = 15 * 60 * 1000

const IMPACT_LABELS = {
  IN_INITIAL_LOAD: 'Carga inicial',
  IN_MANUAL: 'Entrada',
  OUT_SALE: 'Salida',
  OUT_THEFT: 'Robo',
  OUT_MERMA: 'Merma',
  OUT_DAMAGE: 'Dañado',
  OUT_EXPIRED: 'Vencido',
  ADJ_POS: 'Ajuste positivo',
  ADJ_NEG: 'Ajuste negativo',
  TRF_IN: 'Transferencia de entrada',
  TRF_OUT: 'Transferencia de salida'
}

const ENTRY_TYPES = new Set(['IN_INITIAL_LOAD', 'IN_MANUAL', 'TRF_IN'])
const EXIT_TYPES = new Set(['OUT_SALE', 'OUT_THEFT', 'OUT_MERMA', 'OUT_DAMAGE', 'OUT_EXPIRED', 'TRF_OUT'])
const ADJUSTMENT_TYPES = new Set(['ADJ_POS', 'ADJ_NEG'])

const normalizeText = value => String(value ?? '').trim()

const hasValue = value => normalizeText(value) !== '' && normalizeText(value) !== '-'

const resolveMovementDateValue = row =>
  row?.occurredAt ??
  row?.occurred_at ??
  row?.createdAt ??
  row?.created_at ??
  row?.movementDate ??
  row?.movement_date ??
  row?.date ??
  null

const resolveMovementNote = row =>
  row?.note ??
  row?.observacion ??
  row?.observation ??
  row?.description ??
  row?.comentario ??
  ''

const resolveMovementSku = row =>
  row?.sku ??
  row?.item_sku ??
  row?.itemSku ??
  row?.productSku ??
  row?.product_sku ??
  ''

const resolveMovementItemName = row =>
  row?.itemName ??
  row?.item_name ??
  row?.itemNombre ??
  row?.item_nombre ??
  row?.productName ??
  row?.product_name ??
  row?.name ??
  row?.nombre ??
  ''

const resolveMovementProductLabel = row => {
  const sku = normalizeText(resolveMovementSku(row))
  const name = normalizeText(resolveMovementItemName(row))

  if (name && sku) return `${name} · ${sku}`
  if (name) return name
  if (sku) return sku
  return 'Item sin identificar'
}

const getDateTimestamp = value => {
  if (!value) return null

  const parsed = new Date(value).getTime()
  return Number.isNaN(parsed) ? null : parsed
}

const getQuantityNumber = value => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const getQuantityLabel = absoluteQuantity => {
  if (absoluteQuantity === 1) return 'unidad'
  return 'unidades'
}

const getImpactLabel = type => IMPACT_LABELS[type] || getMovementTypeLabel(type)

export const getMovementTone = type => {
  if (ENTRY_TYPES.has(type)) return 'green'
  if (EXIT_TYPES.has(type)) return 'red'
  if (ADJUSTMENT_TYPES.has(type)) return 'neutral'
  return 'blue'
}

export const formatMovementImpact = (type, quantity) => {
  const numeric = getQuantityNumber(quantity)
  const absoluteQuantity = numeric === null ? quantity : Math.abs(numeric)
  const absoluteText = formatQuantity(absoluteQuantity)
  const label = getImpactLabel(type)

  if (numeric === null) {
    return {
      shortLabel: `${label} ${absoluteText}`.trim(),
      longLabel: `${label} ${absoluteText}`.trim()
    }
  }

  const unitLabel = getQuantityLabel(Math.abs(numeric))

  return {
    shortLabel: `${label} ${absoluteText}`,
    longLabel: `${label} de ${absoluteText} ${unitLabel}`
  }
}

const getTechnicalReference = row => {
  const explicitReference = resolveMovementReference(row)
  if (hasValue(explicitReference)) return explicitReference

  return (
    row?.batchCode ??
    row?.batch_code ??
    row?.lote ??
    row?.lot ??
    row?.referenceId ??
    row?.reference_id ??
    ''
  )
}

export const filterMovementRows = (rows, { movementType = '', searchQuery = '' } = {}) => {
  const normalizedSearch = normalizeText(searchQuery).toLowerCase()

  return rows.filter(row => {
    const matchesType = movementType ? resolveMovementType(row) === movementType : true

    if (!matchesType) return false
    if (!normalizedSearch) return true

    const sku = normalizeText(resolveMovementSku(row))
    const itemName = normalizeText(resolveMovementItemName(row))
    const reference = normalizeText(getTechnicalReference(row))
    const note = normalizeText(resolveMovementNote(row))
    const haystack = `${sku} ${itemName} ${reference} ${note}`.toLowerCase()

    return haystack.includes(normalizedSearch)
  })
}

const getGroupReference = row => {
  const explicitReference = getTechnicalReference(row)
  return hasValue(explicitReference) ? normalizeText(explicitReference) : ''
}

const buildNormalizedMovement = (row, index) => {
  const type = resolveMovementType(row)
  const quantityValue = resolveMovementQuantity(row)
  const quantityNumber = getQuantityNumber(quantityValue)
  const absoluteQuantity = quantityNumber === null ? quantityValue : Math.abs(quantityNumber)
  const dateValue = resolveMovementDateValue(row)
  const dateTimestamp = getDateTimestamp(dateValue)
  const sku = normalizeText(resolveMovementSku(row))
  const itemName = normalizeText(resolveMovementItemName(row))
  const reference = getTechnicalReference(row)
  const warehouseLabel = resolveWarehouseLabel(row)
  const note = normalizeText(resolveMovementNote(row))
  const impact = formatMovementImpact(type, quantityValue)

  return {
    id: String(row?.id ?? row?.movementId ?? row?.movement_id ?? `${index}-${sku || 'movement'}`),
    raw: row,
    type,
    typeLabel: getMovementTypeLabel(type),
    tone: getMovementTone(type),
    quantityValue,
    quantityNumber,
    absoluteQuantity,
    absoluteQuantityText: formatQuantity(absoluteQuantity),
    dateValue,
    dateTimestamp,
    dateBucket:
      dateTimestamp === null ? `no-date-${index}` : String(Math.floor(dateTimestamp / INITIAL_LOAD_GROUP_WINDOW_MS)),
    sku,
    itemName,
    productLabel: resolveMovementProductLabel(row),
    productKey: normalizeText(`${itemName}|${sku}`) || String(index),
    warehouseLabel,
    warehouseKey: normalizeText(warehouseLabel || '-'),
    reference: hasValue(reference) ? reference : '',
    groupReference: getGroupReference(row),
    note,
    impactShortLabel: impact.shortLabel,
    impactLongLabel: impact.longLabel
  }
}

const canGroupInitialLoadMovement = movement => movement.type === 'IN_INITIAL_LOAD'

const canJoinInitialLoadGroup = (baseMovement, candidateMovement) => {
  if (!canGroupInitialLoadMovement(candidateMovement)) return false
  if (baseMovement.warehouseKey !== candidateMovement.warehouseKey) return false
  if (baseMovement.dateTimestamp !== null && candidateMovement.dateTimestamp !== null) {
    if (Math.abs(baseMovement.dateTimestamp - candidateMovement.dateTimestamp) > INITIAL_LOAD_GROUP_WINDOW_MS) {
      return false
    }
  }

  if (baseMovement.groupReference && candidateMovement.groupReference) {
    return baseMovement.groupReference === candidateMovement.groupReference
  }

  return baseMovement.dateBucket === candidateMovement.dateBucket
}

const truncateText = (value, maxLength = 90) => {
  const normalized = normalizeText(value)
  if (!normalized || normalized.length <= maxLength) return normalized
  return `${normalized.slice(0, maxLength - 1)}…`
}

const buildSingleMovementEntry = movement => ({
  kind: 'movement',
  id: `movement-${movement.id}`,
  movement,
  dateValue: movement.dateValue,
  dateTimestamp: movement.dateTimestamp,
  tone: movement.tone,
  typeLabel: movement.typeLabel,
  title: movement.productLabel,
  impactLabel: movement.impactLongLabel,
  impactShortLabel: movement.impactShortLabel,
  warehouseLabel: movement.warehouseLabel,
  notePreview: truncateText(movement.note),
  note: movement.note,
  reference: movement.reference
})

const buildGroupedInitialLoadEntry = movements => {
  const firstMovement = movements[0]
  const totalQuantity = movements.reduce((accumulator, movement) => {
    const numeric = getQuantityNumber(movement.quantityValue)
    return accumulator + (numeric === null ? 0 : Math.abs(numeric))
  }, 0)
  const productsCount = new Set(movements.map(movement => movement.productKey)).size
  const notes = Array.from(new Set(movements.map(movement => movement.note).filter(Boolean)))
  const references = Array.from(new Set(movements.map(movement => movement.reference).filter(Boolean)))

  return {
    kind: 'group',
    id: `group-${firstMovement.warehouseKey}-${firstMovement.dateBucket}-${firstMovement.groupReference || firstMovement.id}`,
    typeLabel: firstMovement.typeLabel,
    tone: firstMovement.tone,
    dateValue: firstMovement.dateValue,
    dateTimestamp: firstMovement.dateTimestamp,
    title: `${productsCount} ${productsCount === 1 ? 'producto cargado' : 'productos cargados'}`,
    impactLabel: `+${formatQuantity(totalQuantity)} unidades en total`,
    impactShortLabel: `Carga inicial ${formatQuantity(totalQuantity)}`,
    warehouseLabel: firstMovement.warehouseLabel,
    notePreview: truncateText(notes[0] || ''),
    note: notes.join(' · '),
    reference: references[0] || '',
    productsCount,
    totalQuantityText: formatQuantity(totalQuantity),
    movements
  }
}

export const buildMovementFeed = rows => {
  const normalizedMovements = rows.map(buildNormalizedMovement)
  const feed = []

  let currentIndex = 0

  while (currentIndex < normalizedMovements.length) {
    const currentMovement = normalizedMovements[currentIndex]

    if (!canGroupInitialLoadMovement(currentMovement)) {
      feed.push(buildSingleMovementEntry(currentMovement))
      currentIndex += 1
      continue
    }

    const groupedMovements = [currentMovement]
    let nextIndex = currentIndex + 1

    while (
      nextIndex < normalizedMovements.length &&
      canJoinInitialLoadGroup(currentMovement, normalizedMovements[nextIndex])
    ) {
      groupedMovements.push(normalizedMovements[nextIndex])
      nextIndex += 1
    }

    if (groupedMovements.length > 1) {
      feed.push(buildGroupedInitialLoadEntry(groupedMovements))
    } else {
      feed.push(buildSingleMovementEntry(currentMovement))
    }

    currentIndex = nextIndex
  }

  return feed
}

export const getMovementEmptyState = ({ hasFilters, hasRows }) => {
  if (hasRows) return ''
  if (hasFilters) return 'No encontramos movimientos para esta búsqueda.'
  return 'Aún no hay movimientos registrados.'
}
